/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2017 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * @file
 */
/* global window */
/* eslint-disable max-classes-per-file */

import React, { Component } from 'react';
import {
  action,
  computed,
  observable,
} from 'mobx';
import { observer } from 'mobx-react';

const defaultLocale = 'en-US';
// Get providers from the global environment rather than from an import
// That way we don't have a compile-time dependency and we avoid bi-directional
// dependencies between dc-core and dc-mobx
const core = window.adobe_dc_sdk.appLauncher.coreFunction();
const { logging, locale2 } = core;

const logger = logging.getLogger('LocaleAPI');

/**
 * @classdesc
 * Provides locale info.
 * @class
 */
class LocaleAPI {
  /** The locale specified in the user's preferences */
  @observable userLocale = { locale: undefined };

  constructor() {
    locale2.useState(loc => this.userLocale.locale = loc.locale);
  }

  get cachedLocale() {
    return locale2.cachedLocale;
  }

  set cachedLocale(l) {
    return locale2.cachedLocale = l;
  }

  get cachedRawLocale() {
    return locale2.cachedRawLocale;
  }

  set cachedRawLocale(l) {
    return locale2.cachedRawLocale = l;
  }

  /**
   * Formats given locale string to standard style, e.g. 'fr-FR'
   * @param {string} locale - Locale string w/ two-letter language code,
   *      separator, and two-letter region code
   * @returns {string} Locale string in standard format
   */
  formatLocale(loc) {
    return locale2.formatLocale(loc);
  }

  /**
   * Formats given locale string to standard style, e.g. 'fr-FR', and ensures that the locale is
   * in the set of supported locales.
   * @param {string} locale - Locale string w/ two-letter language code,
   *      separator, and two-letter region code
   * @returns {string} Locale string in standard format
   */
  normalizeLocale(loc) {
    return locale2.normalizeLocale(loc);
  }

  /**
   * @description
   * Determines the "raw" locale based on the following precendence:
   *  1. Query parameter
   *  2. Browser path: /geo/lang
   *  3. HTML local storage
   *  4. Browser default language
   *
   * @method
   * @returns {string} - The normalized raw locale (e.g. de-DE).
   * @public
   */
  getRawLocale() {
    return locale2.getRawLocale();
  }

  /**
   * @description
   * Determines the locale based on the following precendence:
   *  1. Query parameter
   *  2. HTML local storage
   *  3. Browser default language
   *  4. "en-US"
   *
   * @method
   * @returns {string} - The normalized locale (e.g. de-DE).
   * @public
   */
  getLocale() {
    return locale2.getLocale();
  }

  /**
   * Tells whether current locale is in CCJK family
   * @returns {Boolean} True if a CCJK locale
   */
  get isCCJKLocale() {
    return locale2.isCCJKLocale;
  }

  /**
   * Gets the locales supported by DC Web.
   * @returns {Object} - The locales keyed by code (e.g. de-DE) and valued by name (e.g. Deutsch).
   */
  get supportedLocales() {
    return locale2.supportedLocales;
  }

  /**
   * Loads the translations file for the given locale, defaulting to en-US if the translations file for the given
   * locale doesn't exist.
   * @param {Function} load A function which loads a translations file. Takes one parameter, a locale (e.g. de-DE).
   *                        Returns a Promise which resolves with map of translations. Must be given.
   * @param {string} locale The locale whose translations will be loaded (e.g. de-DE). Must be given.
   * @return {Promise} A Promise to load the translations for the given locale.
   */
  loadTranslations(load, loc) {
    if (!load) {
      throw new Error('load function must be given!');
    }
    if (!loc) {
      throw new Error('locale must be given!');
    }

    return load(loc).catch(
      err => {
        if (err.message.search('Cannot find module') !== -1) {
          logger.error(`Cannot find ${loc} translations file. Defaulting to ${defaultLocale}...`);
          return load(defaultLocale);
        }
        throw err;
      },
    );
  }

  /**
   * @description
   * Higher-order component generator which returns the target component with
   * 'locale' and 'messages' properties added. Assumes they're used by target's
   * IntlProvider. Actual loading of messages is done via passed function so
   * that it occurs in the target component's folder.
   * @method
   * @param {Function} WrappedComponent - Component with localized text
   * @param {Function} loadTranslations - Function which loads translations in
   *      context of wrapped component. Takes one parameter, locale, e.g. "fr-FR".
   *      Returns promise which resolves with map of translations.
   * @param {Object} options - May contain:
   *    {String} locale - If supplied, e.g. 'fr-fr', overrides other sources
   *    {boolean} usePromise - If true, the messages passed into WrappedComponent will be wrapped in a Promise.
   * @returns {Object} -  Target react based component with 'locale' and 'messages' properties.
   * @public
   */
  withTranslations(WrappedComponent, loadTranslations, options) {
    const self = this;
    options = options || {};

      @observer
    class Localized extends Component {
        constructor(props, context) {
          super(props, context);
          this.loadMessages();
        }

          /**
           * Current locale
           * @kind computed
           */
          @computed
        get locale() {
          let passedLocale;
          if (options.locale) {
            passedLocale = options.locale;
          }
          return passedLocale ? self.normalizeLocale(passedLocale) : self.getLocale();
        }

          /** Map of localized strings */
          @action
          setMessages(value) {
            this.messages = value;
          }

          @observable messages;

          // Loads translations for current locale. If not 'en-US', loads that too
          // so that we default to that text if translation is not yet available.
          loadMessages() {
            this.messagesPromise = loadTranslations(this.locale).then(msgs => {
              this.setMessages(msgs);
              return msgs;
            }).catch(() => {
              logger.warn(`Missing translation for ${this.locale}`);
            });
          }

          render() {
            if (options.usePromise === true) {
              return <WrappedComponent locale={this.locale} {...this.props} messages={this.messagesPromise} />;
            }
            if (!this.messages) {
              return null;
            }
            return <WrappedComponent locale={this.locale} {...this.props} messages={this.messages} />;
          }
      }

      return Localized;
  }

  /**
   * @description
   * Function searches for supported language example: language is la-ES and we are searching
   * if that language exists in out locale codes
   * @method
   * @param {Array} localeCodes - string array of supported locales
   * @param {string} lang - part of language string example: es
   * @returns {(string|undefined)} if language is supported we are returning language code if not
   * undefined is returned
   * @private
   */
  searchForSupportedLanguage(localeCodes, lang) {
    return locale2.searchForSupportedLanguage(localeCodes, lang);
  }
}

const locale = new LocaleAPI();
// inject this locale instance into the dc-core object so that it can be resolved by code such as:
// import { locale } from 'dc-core';
core.locale = locale;

// eslint-disable-next-line
export { locale };
